/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { createContext, useCallback, useContext, useMemo, useState, type PropsWithChildren } from 'react';

/**
 * CHECKOUT-17 — Kount DDC readiness signal.
 *
 * `<KountDataCollector />` (mounted inside the payment step) writes `true` to
 * this context once Kount's DDC SDK has either fingerprinted the browser
 * (`collected` postMessage event) OR the 5-second timeout has elapsed.
 * `<OrderReviewStep />` reads it to gate the Place Order CTA.
 *
 * The reader and writer are separated into two contexts per the project rule
 * "Split React Contexts by concern" — the writer changes once per checkout,
 * but the reader is consumed in the place-order CTA which would re-render on
 * every value bump if the contexts were merged.
 *
 * Outside the provider tree, `useKountReady()` defaults to `true` so any
 * non-checkout consumer (or a Storybook tree without the provider wired) is
 * never inadvertently blocked. The writer is a no-op outside the provider.
 */

const KountReadyValueContext = createContext<boolean>(true);
const KountReadySetContext = createContext<(ready: boolean) => void>(() => {
    /* no-op outside provider */
});

interface KountReadyProviderProps {
    /**
     * Initial readiness value. Defaults to `false` so the gate is closed until
     * the DDC component flips it. Tests / Storybook can pass `true` to skip the
     * gate.
     */
    initialReady?: boolean;
}

export function KountReadyProvider({ initialReady = false, children }: PropsWithChildren<KountReadyProviderProps>) {
    const [ready, setReadyState] = useState<boolean>(initialReady);

    // Stable setter — only flips false → true once the SDK collects (or times out).
    // Wrapped in a useCallback so consumers don't re-render every parent render just
    // because the function identity changed.
    const setReady = useCallback((next: boolean) => {
        setReadyState((current) => (current === next ? current : next));
    }, []);

    // Memoize the writer context value separately so a re-render of the writer
    // never re-renders the reader, and vice versa.
    const setterValue = useMemo(() => setReady, [setReady]);

    return (
        <KountReadySetContext.Provider value={setterValue}>
            <KountReadyValueContext.Provider value={ready}>{children}</KountReadyValueContext.Provider>
        </KountReadySetContext.Provider>
    );
}

/** Read the current Kount readiness flag. Defaults to `true` outside the provider. */
// eslint-disable-next-line react-refresh/only-export-components
export function useKountReady(): boolean {
    return useContext(KountReadyValueContext);
}

/** Update the Kount readiness flag. No-op outside the provider. */
// eslint-disable-next-line react-refresh/only-export-components
export function useSetKountReady(): (ready: boolean) => void {
    return useContext(KountReadySetContext);
}
